import { useEffect, useState } from "react";
import axios from "axios";
import { Save, Plus, Trash2, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "individual_packs", label: "Individual Token Packs", endpoint: "individual-packs", columns: ["name", "price", "tokens", "per", "badge", "featured"] },
  { id: "assessment_packs", label: "Assessment Token Packs", endpoint: "assessment-packs", columns: ["name", "price", "tokens", "per", "badge", "featured"] },
  { id: "assessment_org_packs", label: "Assessment Organisation Packs", endpoint: "assessment-org-packs", columns: ["name", "users", "price", "tokens", "desc"] },
  { id: "coaching_packs", label: "Coaching Packs", endpoint: "coaching-packs", columns: ["name", "price", "tokens", "desc", "badge", "featured"] },
  { id: "capscan_packs", label: "CapScan Add-ons", endpoint: "capscan-packs", columns: ["name", "price", "tokens", "desc"] },
  { id: "combo_packs", label: "Bundle Packages (Maps + Assessments)", endpoint: "combo-packs", columns: ["name", "price", "tokens", "assessmentTokens", "badge", "desc", "save", "featured"] },
  { id: "smart_bundles", label: "Smart Bundles", endpoint: "smart-bundles", columns: ["name", "price", "intl", "assess", "tag", "badge", "value", "save", "featured", "dark"] },
];

const NUMERIC_COLS = new Set(["price", "tokens", "assessmentTokens", "intl", "assess", "value", "save"]);
const BOOL_COLS = new Set(["featured", "dark", "visible"]);

function blankItem(cols) {
  const o = { visible: true };
  cols.forEach(c => { o[c] = NUMERIC_COLS.has(c) ? 0 : BOOL_COLS.has(c) ? false : ""; });
  return o;
}

function PackTable({ items, columns, onChange, sectionLabel }) {
  const handleField = (idx, col, val) => {
    const next = [...items];
    if (NUMERIC_COLS.has(col)) val = parseInt(val) || 0;
    else if (BOOL_COLS.has(col)) val = !!val;
    next[idx] = { ...next[idx], [col]: val };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, blankItem(columns)]);

  return (
    <div data-testid={`table-${sectionLabel.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="overflow-x-auto rounded-xl border border-navy/10">
        <table className="w-full text-xs">
          <thead className="bg-navy/5">
            <tr>
              {columns.map(c => <th key={c} className="text-left font-dm text-navy/60 uppercase font-bold px-3 py-2">{c}</th>)}
              <th className="text-left font-dm text-navy/60 uppercase font-bold px-3 py-2">visible</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 2} className="text-center font-dm text-navy/40 py-6 italic">No items yet — using hardcoded defaults on Pricing page.</td></tr>
            )}
            {items.map((it, i) => (
              <tr key={i} className="border-t border-navy/5">
                {columns.map(c => (
                  <td key={c} className="px-2 py-2">
                    {BOOL_COLS.has(c) ? (
                      <input type="checkbox" checked={!!it[c]} onChange={(e) => handleField(i, c, e.target.checked)} className="accent-gold w-4 h-4" data-testid={`${sectionLabel.replace(/\s/g, "-").toLowerCase()}-${i}-${c}`} />
                    ) : (
                      <input
                        type={NUMERIC_COLS.has(c) ? "number" : "text"}
                        value={it[c] ?? ""}
                        onChange={(e) => handleField(i, c, e.target.value)}
                        className="w-full border border-navy/10 rounded px-2 py-1 font-dm focus:border-gold focus:outline-none"
                        data-testid={`${sectionLabel.replace(/\s/g, "-").toLowerCase()}-${i}-${c}`}
                      />
                    )}
                  </td>
                ))}
                <td className="px-2 py-2">
                  <input type="checkbox" checked={it.visible !== false} onChange={(e) => handleField(i, "visible", e.target.checked)} className="accent-gold w-4 h-4" />
                </td>
                <td className="px-2 py-2"><button onClick={() => remove(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={add} className="mt-2 inline-flex items-center gap-1 font-dm text-xs font-bold text-gold hover:text-gold/80">
        <Plus className="w-3 h-3" /> Add row
      </button>
    </div>
  );
}

export default function PricingManager() {
  const [cfg, setCfg] = useState(null);
  const [savedFlash, setSavedFlash] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/pricing`, { withCredentials: true });
      setCfg(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const flash = (msg) => { setSavedFlash(msg); setTimeout(() => setSavedFlash(""), 2500); };

  const savePackSection = async (section) => {
    const sec = SECTIONS.find(s => s.id === section);
    if (!sec) return;
    setError("");
    try {
      const r = await axios.put(`${API}/api/admin/pricing/${sec.endpoint}`, { items: cfg[section] || [] }, { withCredentials: true });
      flash(`Saved ${r.data.saved} item(s)`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveActionCosts = async () => {
    setError("");
    try {
      await axios.put(`${API}/api/admin/pricing/action-costs`, cfg.action_costs, { withCredentials: true });
      flash("Action costs saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveSheLeads = async () => {
    setError("");
    try {
      await axios.put(`${API}/api/admin/pricing/sheleads-discount`, cfg.sheleads_discount, { withCredentials: true });
      flash("She Leads discount saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveDisplay = async () => {
    setError("");
    try {
      await axios.put(`${API}/api/admin/pricing/display`, cfg.display, { withCredentials: true });
      flash("Display settings saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const seedDefaults = async (force) => {
    if (force && !window.confirm("This will OVERWRITE all pricing tables with hardcoded defaults. Continue?")) return;
    setError("");
    try {
      const r = await axios.post(`${API}/api/admin/pricing/seed-defaults?force=${force}`, {}, { withCredentials: true });
      const seeded = Object.entries(r.data).filter(([_, v]) => v.action === "seeded").map(([k]) => k);
      flash(seeded.length ? `Seeded: ${seeded.join(", ")}` : "All sections already have data — nothing seeded");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Seed failed");
    }
  };

  if (!cfg) return <div className="bg-white rounded-xl p-10 text-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <PricingManagerInner cfg={cfg} setCfg={setCfg} savePackSection={savePackSection} saveActionCosts={saveActionCosts} saveSheLeads={saveSheLeads} saveDisplay={saveDisplay} seedDefaults={seedDefaults} error={error} savedFlash={savedFlash} reload={load} />
  );
}

function PricingManagerInner({ cfg, setCfg, savePackSection, saveActionCosts, saveSheLeads, saveDisplay, seedDefaults, error, savedFlash, reload }) {
  const [subTab, setSubTab] = useState("packs");
  const ac = cfg.action_costs;
  const sl = cfg.sheleads_discount;
  const disp = cfg.display;

  const setSection = (sec, items) => setCfg({ ...cfg, [sec]: items });
  const setAc = (k, v) => setCfg({ ...cfg, action_costs: { ...ac, [k]: parseInt(v) || 0 } });
  const setSl = (k, v) => setCfg({ ...cfg, sheleads_discount: { ...sl, [k]: v } });
  const setSec = (k, v) => setCfg({ ...cfg, display: { ...disp, sections: { ...(disp.sections || {}), [k]: v } } });
  const setBanner = (k, v) => setCfg({ ...cfg, display: { ...disp, banner: { ...(disp.banner || {}), [k]: v } } });
  const setFeatured = (v) => setCfg({ ...cfg, display: { ...disp, featured_bundle_name: v } });

  const bundleNames = (cfg.smart_bundles && cfg.smart_bundles.length > 0
    ? cfg.smart_bundles
    : [{ name: "Explorer" }, { name: "Coach & Grow" }, { name: "Professional" }, { name: "Team Intelligence" }, { name: "Workforce Readiness" }, { name: "Enterprise" }]
  ).map(b => b.name);

  const subTabs = [
    { id: "packs", label: "Individual Token Packs" },
    { id: "costs", label: "CapScan & Coaching Costs" },
    { id: "bundles", label: "Bundles" },
    { id: "sheleads", label: "She Leads Pricing" },
    { id: "display", label: "Display Settings" },
  ];

  return (
    <div data-testid="pricing-manager">
      {error && <div className="bg-red-50 border-l-4 border-red-500 rounded-r p-3 mb-3 font-dm text-red-700 text-xs">{error}</div>}
      {savedFlash && <div className="bg-green-50 border-l-4 border-green-500 rounded-r p-3 mb-3 font-dm text-green-700 text-xs flex items-center gap-2"><Check className="w-4 h-4" /> {savedFlash}</div>}

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="font-dm text-navy/50 text-xs">Edit pricing tables, action costs, She Leads discount, and display rules. Changes apply instantly to <code className="bg-navy/5 px-1.5 py-0.5 rounded">/pricing</code>.</p>
        <button onClick={() => seedDefaults(true)} data-testid="reset-pricing-defaults" className="border border-red-300 text-red-600 hover:bg-red-50 font-dm text-xs font-bold px-3 py-1.5 rounded">Reset all to hardcoded defaults</button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-4 border-b border-navy/10">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`pm-subtab-${t.id}`}
            className={`font-dm text-xs px-3 py-2 rounded-t whitespace-nowrap ${subTab === t.id ? "bg-navy text-gold font-bold" : "text-navy/60 hover:text-navy"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Individual Token Packs + Assessment + Coaching + CapScan + Combo */}
      {subTab === "packs" && (
        <div className="space-y-6">
          {SECTIONS.filter(s => ["individual_packs", "assessment_packs", "assessment_org_packs", "coaching_packs", "capscan_packs"].includes(s.id)).map(sec => (
            <div key={sec.id} className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-playfair text-navy font-bold">{sec.label}</h3>
                <button onClick={() => savePackSection(sec.id)} data-testid={`save-${sec.id}`} className="bg-navy text-gold font-dm font-bold text-xs px-3 py-1.5 rounded flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
              </div>
              <PackTable items={cfg[sec.id] || []} columns={sec.columns} onChange={(items) => setSection(sec.id, items)} sectionLabel={sec.label} />
            </div>
          ))}
        </div>
      )}

      {subTab === "costs" && (
        <div className="bg-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-playfair text-navy font-bold">CapScan & Coaching Token Costs</h3>
            <button onClick={saveActionCosts} data-testid="save-action-costs" className="bg-navy text-gold font-dm font-bold text-xs px-3 py-1.5 rounded flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
          </div>
          <p className="font-dm text-navy/50 text-xs mb-4">Edit how many tokens each action costs. Affects backend deduction immediately on save.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["capscan_individual", "Individual CapScan", "intelligence"],
              ["capscan_team_small", "Team CapScan ≤ 10 people", "intelligence"],
              ["capscan_team_large", "Team CapScan 11–20 people", "intelligence"],
              ["capscan_rescan", "Re-scan (individual)", "intelligence"],
              ["coaching_15", "Coaching — 15 min session", "assessment"],
              ["coaching_30", "Coaching — 30 min session", "assessment"],
              ["coaching_45", "Coaching — 45 min session", "assessment"],
              ["coaching_60", "Coaching — 60 min session", "assessment"],
            ].map(([k, lab, kind]) => (
              <div key={k}>
                <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">{lab}</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="50" value={ac[k] ?? 0} onChange={(e) => setAc(k, e.target.value)} data-testid={`ac-${k}`}
                    className="w-24 border border-navy/20 rounded px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                  <span className="font-dm text-navy/40 text-[10px] uppercase">{kind} tokens</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === "bundles" && (
        <div className="space-y-6">
          {SECTIONS.filter(s => ["combo_packs", "smart_bundles"].includes(s.id)).map(sec => (
            <div key={sec.id} className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-playfair text-navy font-bold">{sec.label}</h3>
                <button onClick={() => savePackSection(sec.id)} data-testid={`save-${sec.id}`} className="bg-navy text-gold font-dm font-bold text-xs px-3 py-1.5 rounded flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
              </div>
              <PackTable items={cfg[sec.id] || []} columns={sec.columns} onChange={(items) => setSection(sec.id, items)} sectionLabel={sec.label} />
            </div>
          ))}
        </div>
      )}

      {subTab === "sheleads" && (
        <div className="bg-white rounded-xl p-5 max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-playfair text-navy font-bold">She Leads Discount</h3>
            <button onClick={saveSheLeads} data-testid="save-sheleads" className="bg-navy text-gold font-dm font-bold text-xs px-3 py-1.5 rounded flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
          </div>
          <p className="font-dm text-navy/50 text-xs mb-5">Discount overlay applied only to users with <code className="bg-navy/5 px-1.5 py-0.5 rounded">gender_verified = "Female"</code>. Not visible to other users.</p>
          <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="sheleads-active-toggle">
            <input type="checkbox" checked={!!sl.active} onChange={(e) => setSl("active", e.target.checked)} className="accent-gold w-4 h-4" />
            <span className="font-dm text-navy text-sm">Active</span>
          </label>
          <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">Discount percentage (0–60%)</label>
          <input type="number" min="0" max="60" value={sl.discount ?? 0} onChange={(e) => setSl("discount", parseInt(e.target.value) || 0)} data-testid="sheleads-discount-input"
            className="w-32 border border-navy/20 rounded px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
        </div>
      )}

      {subTab === "display" && (
        <div className="space-y-5 max-w-2xl">
          <div className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-playfair text-navy font-bold">Section Visibility</h3>
              <button onClick={saveDisplay} data-testid="save-display" className="bg-navy text-gold font-dm font-bold text-xs px-3 py-1.5 rounded flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
            </div>
            <p className="font-dm text-navy/50 text-xs mb-4">Toggle which sections appear on the public /pricing page.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                ["individual", "Individual Token Packs"],
                ["assessment", "Assessment Token Packs"],
                ["coaching", "Coaching Packs"],
                ["capscan", "CapScan Add-ons"],
                ["bundles", "Bundles & Smart Bundles"],
                ["sheleads", "She Leads section (informational)"],
              ].map(([k, lab]) => (
                <label key={k} className="flex items-center gap-2 font-dm text-navy text-sm cursor-pointer" data-testid={`display-section-${k}`}>
                  <input type="checkbox" checked={(disp.sections || {})[k] !== false} onChange={(e) => setSec(k, e.target.checked)} className="accent-gold w-4 h-4" />
                  {lab}
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl p-5">
            <h3 className="font-playfair text-navy font-bold mb-4">Announcement Banner</h3>
            <label className="flex items-center gap-2 mb-3 font-dm text-navy text-sm cursor-pointer" data-testid="banner-active-toggle">
              <input type="checkbox" checked={!!(disp.banner || {}).active} onChange={(e) => setBanner("active", e.target.checked)} className="accent-gold w-4 h-4" /> Banner active
            </label>
            <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">Banner text</label>
            <input value={(disp.banner || {}).text || ""} onChange={(e) => setBanner("text", e.target.value)} placeholder="e.g. Black Friday: 20% off all packs" data-testid="banner-text"
              className="w-full border border-navy/20 rounded px-3 py-2 font-dm text-sm mb-3 focus:border-gold focus:outline-none" />
            <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">Colour</label>
            <select value={(disp.banner || {}).colour || "navy"} onChange={(e) => setBanner("colour", e.target.value)} data-testid="banner-colour"
              className="border border-navy/20 rounded px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none">
              <option value="navy">Navy</option>
              <option value="gold">Gold</option>
              <option value="green">Green</option>
            </select>
          </div>

          <div className="bg-white rounded-xl p-5">
            <h3 className="font-playfair text-navy font-bold mb-4">"Most Popular" Bundle Highlight</h3>
            <p className="font-dm text-navy/50 text-xs mb-3">Choose which Smart Bundle gets the highlighted featured treatment.</p>
            <select value={disp.featured_bundle_name || ""} onChange={(e) => setFeatured(e.target.value)} data-testid="featured-bundle"
              className="w-full sm:w-72 border border-navy/20 rounded px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none">
              <option value="">— None —</option>
              {bundleNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <button onClick={saveDisplay} className="bg-navy text-gold font-dm font-bold text-sm px-5 py-2.5 rounded flex items-center gap-2"><Save className="w-4 h-4" />Save all display settings</button>
        </div>
      )}
    </div>
  );
}
